import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type SecurityCheckType = 'MONTHLY' | 'PC_PATCH';

export type SecurityCheckStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export const SecurityCheckTypeLabel: Record<SecurityCheckType, string> = {
  MONTHLY: '月次セキュリティチェック',
  PC_PATCH: 'PCセキュリティパッチ適用確認',
} as const;

export const SecurityCheckStatusLabel: Record<SecurityCheckStatus, string> = {
  NOT_STARTED: '未実施',
  IN_PROGRESS: '実施中',
  COMPLETED: '完了',
} as const;

export type ListSecurityCheckParams = {
  financialYear: number;
};

export type SecurityCheckDetailParams = {
  financialYear: number;
  month: number;
};

export type StartSecurityCheckParams = {
  financialYear: number;
  month: number;
};

export type RemindSecurityCheckParams = {
  securityCheckType: SecurityCheckType;
  financialYear: number;
  month: number;
  userIds: string[];
};

export type ListSecurityCheckResultParams = {
  includeCompleted: boolean;
  securityCheckType?: SecurityCheckType;
};

export type ExecuteSecurityCheckParams = {
  results: ExecuteSecurityCheckResultItem[];
};

export type ExecuteSecurityCheckResultItem = {
  securityCheckResultId: string;
  resultCode: string;
};

// --- Response Types ---

export type SecurityCheckListResult = {
  financialYear: number;
  month: number;
  sortOrder: number;
  monthlyStatus: SecurityCheckStatus;
  pcPatchStatus: SecurityCheckStatus;
};

export type SecurityCheckMonthlyResultItem = {
  securityCheckResultId: string;
  userId: string;
  userName: string;
  resultCode: string | null;
  resultCodeName: string | null;
  completedAt: string | null;
};

export type SecurityCheckPcResultItem = {
  securityCheckResultId: string;
  userId: string;
  userName: string;
  assessmentId: string;
  assessmentName: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  resultCode: string | null;
  resultCodeName: string | null;
  completedAt: string | null;
};

export type SecurityCheckTypeDetail = {
  status: SecurityCheckStatus;
  results: SecurityCheckMonthlyResultItem[];
};

export type SecurityCheckPcDetail = {
  status: SecurityCheckStatus;
  results: SecurityCheckPcResultItem[];
};

export type SecurityCheckDetailResult = {
  monthly: SecurityCheckTypeDetail;
  pcPatch: SecurityCheckPcDetail;
};

export type SecurityCheckResultListItem = {
  securityCheckResultId: string;
  securityCheckType: SecurityCheckType;
  title: string;
  financialYear: number;
  month: number;
  assessmentId?: string;
  assessmentName?: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  resultCode: string | null;
  completedAt: string | null;
};

export type SecurityCheckResultDetail = {
  securityCheckResultId: string;
  securityCheckType: SecurityCheckType;
  title: string;
  content: string;
  financialYear: number;
  month: number;
  assessmentId?: string;
  assessmentName?: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  resultCode: string | null;
  completedAt: string | null;
};

// --- API関数 ---

/**
 * セキュリティチェック一覧取得API（管理者）
 * @param params.financialYear - 年度（必須）
 */
export const listSecurityCheckAdmin = async (
  params: ListSecurityCheckParams,
): Promise<SecurityCheckListResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_SECURITY_CHECK_ADMIN, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * セキュリティチェック詳細取得API（管理者）
 * @param params.financialYear - 年度（必須）
 * @param params.month - 月（必須）
 */
export const getSecurityCheckDetail = async (
  params: SecurityCheckDetailParams,
): Promise<SecurityCheckDetailResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_SECURITY_CHECK_DETAIL, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * セキュリティチェック開始API（管理者）
 * @param params.financialYear - 年度（必須）
 * @param params.month - 月（必須）
 */
export const startSecurityCheck = async (params: StartSecurityCheckParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.START_SECURITY_CHECK, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * セキュリティチェックリマインドAPI（管理者）
 * @param params.securityCheckType - セキュリティチェック種別（必須, MONTHLY / PC_PATCH）
 * @param params.financialYear - 年度（必須）
 * @param params.month - 月（必須）
 * @param params.userIds - リマインド対象ユーザーIDリスト（必須）
 */
export const remindSecurityCheck = async (params: RemindSecurityCheckParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.REMIND_SECURITY_CHECK, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * セキュリティチェック結果一覧取得API（ユーザー）
 * @param params.includeCompleted - 実施済みを含む（必須, true: 実施済みも含む）
 * @param params.securityCheckType - セキュリティチェック種別（任意, MONTHLY / PC_PATCH, 未指定で全件）
 */
export const listSecurityCheckResult = async (
  params: ListSecurityCheckResultParams,
): Promise<SecurityCheckResultListItem[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_SECURITY_CHECK_RESULT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * セキュリティチェック結果取得API（ユーザー）
 * @param securityCheckResultId - セキュリティチェック結果ID（必須, パスパラメータ）
 */
export const getSecurityCheckResult = async (
  securityCheckResultId: string,
): Promise<SecurityCheckResultDetail> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.get(
      `${KanzhongBackend.GET_SECURITY_CHECK_RESULT}/${securityCheckResultId}`,
      {
        headers: {
          'X-Session-Id': sessionId,
        },
      },
    );

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * セキュリティチェック実施API
 * @param params.results - 実施結果リスト（必須）
 * @param params.results[].securityCheckResultId - セキュリティチェック結果ID（必須, 最大300文字）
 * @param params.results[].resultCode - 結果コード（必須, 最大300文字, Enum: CONFIRMED/UNUSED/INITIALIZED/BROKEN/DISPOSED）
 */
export const executeSecurityCheck = async (params: ExecuteSecurityCheckParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.EXECUTE_SECURITY_CHECK, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
